import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './TicketTableDashboard.css';
import './UI/SearchInput.css';
import { useTranslation } from 'react-i18next';
import { FaInstagram, FaFacebookMessenger, FaWhatsapp, FaEnvelope, FaGlobe, FaSort, FaSortUp, FaSortDown, FaUser, FaRobot, FaPlusCircle, FaSearch } from 'react-icons/fa';

const TicketTableDashboard = ({ 
    tickets, 
    assignedTickets, 
    user, 
    onTicketClick, 
    compact = false, 
    hasFaAccount = false, 
    onCreateTicket, 
    brands = [], 
    selectedBrand = null, 
    onBrandChange = null, 
    pagination, 
    assignedPagination,
    onPageChange,
    onFilterChange,
    currentViewMode = 'unassigned',
    onSearchStateChange = null
}) => {

  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL parameters
  const [sortOrder, setSortOrder] = useState(searchParams.get('sortOrder') || 'desc');
  const [sortField, setSortField] = useState(searchParams.get('sortField') || 'updated_at');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('statusFilter') || '');
  const [searchFilter, setSearchFilter] = useState(searchParams.get('searchFilter') || '');
  
  // Set default active tab based on user type, but check URL first
  const getDefaultActiveTab = () => {
    const urlTab = searchParams.get('activeTab');
    if (urlTab) return urlTab;
    
    if (hasFaAccount) {
      return 'Après vente'; // FA users default to postsales
    } else {
      return 'General'; // Business users and EDN users default to General
    }
  };
  
  const [activeTopicTab, setActiveTopicTab] = useState(getDefaultActiveTab());
  const [useDateCreated, setUseDateCreated] = useState(searchParams.get('useDateCreated') === 'true');
  const [searchResults, setSearchResults] = useState(null);

  const ticketsPerPage = 20;

  const ASSIGNED_TO_ME_TAB = 'Assigned to me';
  const GENERAL_TAB = 'General';
  const CHATBOT_TAB = 'chatbot';

  const channelIcons = {
    Instagram: FaInstagram,
    Messenger: FaFacebookMessenger,
    WhatsApp: FaWhatsapp,
    Gmail: FaEnvelope,
    Outlook: FaEnvelope,
    Website: FaGlobe,
    Custom: FaEnvelope
  };

  const statusOptions = {
    new: 'newTickets',
    automated: 'automatedTickets',
    waiting_client_response: 'waitingClientResponse',
    waiting_carrier_response: 'waitingCarrierResponse',
    waiting_carrier_commercial_response: 'waitingCarrierCommercialResponse',
    npec_yourstore: 'npecYourStore',
    resolved: 'resolvedTickets',
    affected: 'affectedTickets',
    closed: 'closedTickets',
    open: 'openTickets',
    pending: 'pendingTickets',
    return_pending: 'returnPending',
    to_compensate: 'toCompensate',
    processing: 'processing',
    compensation: 'compensation',
    compensation_invoice_made: 'compensationInvoiceMade',
    compensation_in_progress: 'compensationInProgress'
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const sortTickets = (tickets, field, order) => {
    return [...tickets].sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (field === 'updated_at' || field === 'created_at') {
        return order === 'asc' ? new Date(valA) - new Date(valB) : new Date(valB) - new Date(valA);
      }
      if (field === 'sentiment_score') {
        return order === 'asc' ? valA - valB : valB - valA;
      }
      return order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
  };

  const filterTickets = (tickets) => {
    // Backend now handles topic and assigned separation
    // Only handle frontend filtering for search results and brand
    
    if (searchResults !== null) {
      // If we have search results, apply local filtering to those
      return searchResults.filter(ticket => {
        // Brand filtering (still frontend until backend supports it)
        let brandMatch = true;
        if (selectedBrand && ticket.brand_id) {
          brandMatch = ticket.brand_id === selectedBrand;
        }
        
        return brandMatch;
      });
    }
    
    // Apply minimal frontend filtering (brand only, status now handled by backend)
    return tickets.filter(ticket => {
      // Brand filtering (still frontend until backend supports it)
      let brandMatch = true;
      if (selectedBrand && ticket.brand_id) {
        brandMatch = ticket.brand_id === selectedBrand;
      }
      
      return brandMatch;
    });
  };

  const handleDateFieldToggle = (e) => {
    const useCreated = e.target.checked;
    setUseDateCreated(useCreated);
    updateUrlParams({ useDateCreated: useCreated });
    
    // Update sort field if currently sorting by date
    if (sortField === 'updated_at' || sortField === 'created_at') {
      setSortField(useCreated ? 'created_at' : 'updated_at');
    }
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  const getCurrentDateField = () => {
    return useDateCreated ? 'created_at' : 'updated_at';
  };

  const handleSortChange = (field) => {
    // If clicking on date field, use the currently selected date field
    if (field === 'date') {
      field = getCurrentDateField();
    }
    const newSortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(newSortOrder);
    updateUrlParams({ sortField: field, sortOrder: newSortOrder });
  };

  const handleStatusFilterChange = (e) => {
    const newStatusFilter = e.target.value;
    setStatusFilter(newStatusFilter);
    updateUrlParams({ statusFilter: newStatusFilter });
    
    // Trigger search with updated status filter
    const searchCriteria = {
      query: searchFilter,
      ticket_status: newStatusFilter
    };
    performSearch(searchCriteria);
  };

  // Simplified search function that only uses query parameter
  const performSearch = async (searchData = null) => {
    const searchCriteria = searchData || {
      query: searchFilter,
      ticket_status: statusFilter
    };

    // Remove empty search criteria
    const filteredCriteria = Object.entries(searchCriteria)
      .filter(([key, value]) => value && value.trim() !== '')
      .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {});

    if (Object.keys(filteredCriteria).length === 0) {
      setSearchResults(null);
      // Notify parent that search is inactive
      if (onSearchStateChange) {
        onSearchStateChange(false);
      }
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const queryParams = new URLSearchParams(filteredCriteria).toString();
      const response = await axios.get(`/api/tickets/tickets/search?${queryParams}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSearchResults(response.data.tickets || []);
      // Notify parent that search is active
      if (onSearchStateChange) {
        onSearchStateChange(true);
      }
    } catch (error) {
      console.error('Error searching tickets:', error);
      setSearchResults([]);
      // Still notify that search is active even if failed
      if (onSearchStateChange) {
        onSearchStateChange(true);
      }
    }
  };

  const handleSearchFilterChange = (e) => {
    const searchTerm = e.target.value;
    setSearchFilter(searchTerm);
    updateUrlParams({ searchFilter: searchTerm });

    // Clear search results if input is empty
    if (searchTerm.trim() === '') {
      setSearchResults(null);
      // Notify parent that search is inactive
      if (onSearchStateChange) {
        onSearchStateChange(false);
      }
    }
  };

  // Handle search submission
  const handleSearchSubmit = async () => {
    if (searchFilter.trim() === '') {
      setSearchResults(null);
      if (onSearchStateChange) {
        onSearchStateChange(false);
      }
      return;
    }
    
    await performSearch({ query: searchFilter, ticket_status: statusFilter });
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  const clearSearchFilter = () => {
    setSearchFilter('');
    setSearchResults(null);
    
    // Notify parent that search is inactive
    if (onSearchStateChange) {
      onSearchStateChange(false);
    }
    
    // Clear URL params
    updateUrlParams({ searchFilter: '' });
  };

  const getSentimentScoreClass = (score) => {
    if (score <= 25) return 'low';
    if (score <= 60) return 'medium';
    return 'high';
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <FaSort />;
    return sortOrder === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const handleTopicTabChange = (topic) => {
    setActiveTopicTab(topic);
    updateUrlParams({ activeTab: topic });
    
    // Now all users except experience users can use backend filtering
    if (onFilterChange && user?.role !== 'experience') {
        let newFilters = {};
        
        if (topic === ASSIGNED_TO_ME_TAB) {
            // Switch to assigned tickets view
            newFilters = {
                topics: [], // No topic filter for assigned view
                separate_assigned: true,
                viewMode: 'assigned'
            };
        } else if (topic === CHATBOT_TAB) {
            // Show only chatbot tickets in unassigned view
            newFilters = {
                topics: ['chatbot'],
                separate_assigned: true,
                viewMode: 'unassigned'
            };
        } else if (topic === GENERAL_TAB) {
            // For EDN users, show all unassigned tickets
            newFilters = {
                topics: [], // No topic filter
                separate_assigned: true,
                viewMode: 'unassigned'
            };
        } else {
            // Regular topic filtering (e.g., "Après vente", "Avant vente")
            newFilters = {
                topics: [topic],
                separate_assigned: true,
                viewMode: 'unassigned'
            };
        }
        
        onFilterChange(newFilters);
    }
  };

  const handleBrandChange = (brandId) => {
    if (onBrandChange) {
      onBrandChange(brandId);
    }
  };

  // Get the current tickets based on view mode
  const getCurrentTickets = () => {
    if (currentViewMode === 'assigned') {
      return assignedTickets || [];
    } else {
      return tickets || [];
    }
  };

  // Get the current pagination based on view mode
  const getCurrentPagination = () => {
    if (currentViewMode === 'assigned') {
      return assignedPagination;
    } else {
      return pagination;
    }
  };

  const displayedTickets = searchResults !== null ? searchResults : getCurrentTickets();
  const sortedAndFilteredTickets = sortTickets(filterTickets(displayedTickets), sortField, sortOrder);

  const handlePageChange = (direction) => {
    const currentPagination = getCurrentPagination();
    const currentPage = currentPagination?.current_page || 1;
    
    if (direction === 'next' && currentPagination?.has_next) {
      onPageChange(currentPage + 1);
    } else if (direction === 'prev' && currentPagination?.has_previous) {
      onPageChange(currentPage - 1);
    }
  };

  // Update URL parameters when filters change
  const updateUrlParams = (updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === null || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value.toString());
      }
    });
    
    setSearchParams(newParams, { replace: true });
  };

  // Simplified useEffect to handle search on mount
  useEffect(() => {
    const hasSimpleSearchParam = searchParams.get('searchFilter') && searchParams.get('searchFilter').trim() !== '';
    const hasStatusFilter = searchParams.get('statusFilter') && searchParams.get('statusFilter').trim() !== '';
    
    if (hasSimpleSearchParam || hasStatusFilter) {
      // Trigger search with URL parameters
      performSearch();
    }
  }, []); // Run only on mount

  return (
    <div className="ticket-table-dashboard">
      {/* Brand tabs - shown only if user has brands */}
      {brands.length > 0 && (
        <div className="brand-tabs">
          {brands.map(brand => (
            <button
              key={brand.id}
              className={`brand-tab ${selectedBrand === brand.id ? 'active' : ''}`}
              onClick={() => handleBrandChange(brand.id)}
            >
              {brand.name}
            </button>
          ))}
        </div>
      )}

      {/* Show topic tabs for all users except experience users */}
      {user?.role !== 'experience' && (
        <div className="topic-tabs">
            {hasFaAccount && (
                <>
                    <button 
                        className={`topic-tab ${activeTopicTab === 'Après vente' ? 'active' : ''}`}
                        onClick={() => handleTopicTabChange('Après vente')}
                    >
                        {t('postsales', 'Postsales')}
                    </button>
                    <button 
                        className={`topic-tab ${activeTopicTab === 'Avant vente' ? 'active' : ''}`}
                        onClick={() => handleTopicTabChange('Avant vente')}
                    >
                        {t('presales', 'Presales')}
                    </button>
                </>
            )}
            {/* Show General tab for EDN users, business users, and employee users */}
            {(user?.edn_api_account || (!hasFaAccount && !user?.edn_api_account) || user?.role === 'employee') && (
                <button
                    className={`topic-tab ${activeTopicTab === GENERAL_TAB ? 'active' : ''}`}
                    onClick={() => handleTopicTabChange(GENERAL_TAB)}
                >
                    {t('general', 'General')}
                </button>
            )}
            <button
                className={`topic-tab ${activeTopicTab === CHATBOT_TAB ? 'active' : ''}`}
                onClick={() => handleTopicTabChange(CHATBOT_TAB)}
            >
                {t('chatbot', 'Chatbot')}
            </button>
            {/* Show Assigned to me tab for all users (EDN, business, FA users, and employee users) */}
            <button
                className={`topic-tab ${activeTopicTab === ASSIGNED_TO_ME_TAB ? 'active' : ''}`}
                onClick={() => handleTopicTabChange(ASSIGNED_TO_ME_TAB)}
            >
                {t('assignedToMe', 'Assigned to me')}
            </button>
        </div>
      )}
      
      <div className="table-controls">
        <div className="filters-container">
          <div className="status-filter">
            <select
              id="status-select"
              value={statusFilter}
              onChange={handleStatusFilterChange}
            >
              <option value="">{t('all')}</option>
              {Object.entries(statusOptions).map(([status, translationKey]) => (
                <option key={status} value={status}>
                  {t(translationKey)}
                </option>
              ))}
            </select>
          </div>
          
          {/* Simplified Search Controls */}
          <div className="company-filter">
            <div className="search-container">
              <input
                id="search-input"
                type="text"
                value={searchFilter}
                onChange={handleSearchFilterChange}
                onKeyPress={handleKeyPress}
                placeholder={t('searchGeneral', 'Search across all fields...')}
                className="search-input"
              />
              <button
                onClick={handleSearchSubmit}
                className="search-submit-button"
                title={t('search', 'Search')}
              >
                <FaSearch />
              </button>
              {searchFilter && (
                <button
                  onClick={clearSearchFilter}
                  className="clear-search-button"
                  title={t('clearSearch', 'Clear search')}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          
          {user?.edn_api_account && !hasFaAccount && onCreateTicket && (
            <div className="create-ticket-filter">
              <button 
                onClick={onCreateTicket} 
                className="una-create-ticket-button"
              >
                <FaPlusCircle style={{ marginRight: '8px' }} /> {t('createEdnTicket', 'Create EDN Ticket')}
              </button>
            </div>
          )}
        </div>
        <div className="pagination">
          <button 
            onClick={() => handlePageChange('prev')} 
            disabled={!getCurrentPagination()?.has_previous}
          >
            {t('common.previous')}
          </button>
          <span>
            {getCurrentPagination()?.current_page || 1} / {getCurrentPagination()?.total_pages || 1}
          </span>
          <button 
            onClick={() => handlePageChange('next')} 
            disabled={!getCurrentPagination()?.has_next}
          >
            {t('common.next')}
          </button>
        </div>
      </div>

      <table>
        <thead>
          <tr>
            <th>{t('channel')}</th>
            <th>{t('subject')}</th>
            <th 
              onClick={() => handleSortChange('sentiment_score')} 
              className="sortable"
            >
              {t('sentimentScore')} <SortIcon field="sentiment_score" />
            </th>
            {hasFaAccount && (
              <th>
                {currentViewMode === 'assigned' ? t('employee', 'Employee') : t('topic', 'Topic')}
              </th>
            )}
            <th>{t('carrier')}</th>
            {!hasFaAccount && <th>{t('company')}</th>}
            <th>{t('status')}</th>
            <th 
              className="sortable date-header-with-checkbox"
            >
              <div className="date-header-content">
                <span onClick={() => handleSortChange('date')}>
                  {useDateCreated ? t('createdAt') : t('updatedAt')} <SortIcon field={getCurrentDateField()} />
                </span>
                <label className="inline-date-checkbox">
                  <input
                    type="checkbox"
                    checked={useDateCreated}
                    onChange={handleDateFieldToggle}
                    onClick={(e) => e.stopPropagation()}
                  />
                </label>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedAndFilteredTickets.map((ticket) => {
            const ChannelIcon = channelIcons[ticket.channel_name];
            const dateToShow = useDateCreated ? ticket.created_at : ticket.updated_at;
            return (
              <tr key={ticket._id} onClick={() => onTicketClick(ticket._id)}>
                <td className="tertiary-info">
                  <div className="visual-indicator">
                    {ChannelIcon && <ChannelIcon className="channel-icon-dashboard" />}
                    <span>{ticket.channel_name}</span>
                  </div>
                </td>
                <td className="primary-info truncate">{ticket.subject}</td>
                <td>
                  <span className={`sentiment-score ${getSentimentScoreClass(ticket.sentiment_score)}`}>
                    {Math.round(ticket.sentiment_score)}
                  </span>
                </td>
                {hasFaAccount && (
                  <td className="secondary-info">
                    {currentViewMode === 'assigned' ? (
                      <span className="employee-name">
                        {ticket.employee_name || t('unknown', 'Unknown')}
                      </span>
                    ) : (
                      <span className={`topic-badge ${
                        ticket.topic === 'Avant vente' ? 'presales' : 
                        ticket.topic === 'chatbot' ? 'chatbot' : 'postsales'
                      }`}>
                        {ticket.topic === 'Avant vente' ? t('presales', 'Presales') : 
                         ticket.topic === 'chatbot' ? t('chatbot', 'Chatbot') : 
                         t('postsales', 'Postsales')}
                      </span>
                    )}
                  </td>
                )}
                <td className="secondary-info">{ticket.carrier_name || t('unknown')}</td>
                {!hasFaAccount && <td className="secondary-info">{ticket.company_name || t('unknown')}</td>}
                <td className="centered">
                  <span className={`status-badge ${ticket.status}`}>
                    {t(ticket.status)}
                  </span>
                </td>
                <td className="tertiary-info date-info">{formatDate(dateToShow)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default TicketTableDashboard;